import {
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns'
import type { Deal } from '@/types'

export function getDealCalendarDate(deal: Deal): Date | null {
  const raw = deal.dataReuniao || deal.dataFechamentoPrevisto
  if (!raw) return null
  const parsed = typeof raw === 'string' ? parseISO(raw) : new Date(raw)
  if (!isValid(parsed)) return null
  return startOfDay(parsed)
}

export function dayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

export function buildDealsByDayKey(deals: Deal[]): Map<string, Deal[]> {
  const map = new Map<string, Deal[]>()
  for (const deal of deals) {
    const day = getDealCalendarDate(deal)
    if (!day) continue
    const key = dayKey(day)
    const list = map.get(key) ?? []
    list.push(deal)
    map.set(key, list)
  }
  for (const list of map.values()) {
    list.sort((a, b) => {
      const da = getDealCalendarDate(a)?.getTime() ?? 0
      const db = getDealCalendarDate(b)?.getTime() ?? 0
      return da - db
    })
  }
  return map
}

export function pickInitialSelectedDay(
  dealsByDay: Map<string, Deal[]>,
  viewMonth: Date,
): Date | null {
  const inMonth = [...dealsByDay.entries()]
    .map(([key, list]) => ({ key, day: getDealCalendarDate(list[0])!, list }))
    .filter((x) => x.day && isSameMonth(x.day, viewMonth))
    .sort((a, b) => a.day.getTime() - b.day.getTime())

  if (inMonth.length > 0) return inMonth[0].day

  const all = [...dealsByDay.entries()]
    .map(([, list]) => getDealCalendarDate(list[0])!)
    .filter(Boolean)
    .sort((a, b) => a.getTime() - b.getTime())

  return all[0] ?? null
}

export function dealsOnDay(
  dealsByDay: Map<string, Deal[]>,
  day: Date | null,
): Deal[] {
  if (!day) return []
  return dealsByDay.get(dayKey(day)) ?? []
}

export function isToday(day: Date): boolean {
  return isSameDay(day, new Date())
}
